<template>
    <SCSidebar v-model:visible="visible" title="Cluster Updates">
        <div v-if="!loaded">Loading...</div>
        <!-- same structure as UpdateStatusModal: to the user these are states
             of the same sidebar, so the look and data must match -->
        <SCForm v-if="loaded" saveLabel="Start Update" cancelLabel="Cancel" :showCancel="true" :saveDisabled="!selectedVersion" @save="handleApplySave" @cancel="handleDialogClose">
            <SCFormStep>
            <SCPropertyList stacked>
                <SCProperty label="Cluster Name" :value="cluster.data.name" />
                <SCProperty label="Current Version" :value="cluster.data.version" />
            </SCPropertyList>
            <SCRadioGroup title="Available Update(s)" :showSeparator="true" :modelValue="selectedVersion">
                <template #group>
                    <SCRadio v-for="update in cluster.data.updatesAvailableOptions" :key="update.uuid" name="selectedVersion" v-model="selectedVersion" :value="update.uuid">
                        <div v-html="update.description"></div>
                        <template #description>
                            <SCAccordion title="Release Notes" v-if="update.changeLog.length">
                                <SCParagraph v-html="update.changeLog"></SCParagraph>
                            </SCAccordion>
                            <SCParagraph v-else class="scale-no-release-notes">No release notes available</SCParagraph>
                        </template>
                    </SCRadio>
                </template>
            </SCRadioGroup>
            </SCFormStep>
        </SCForm>
    </SCSidebar>
</template>

<script>
import EventBus from '@/layouts/slots/event-bus';
import { markUpdateSent } from '@/lib/cluster-updates';
import { cluster } from '@/lib/nucleus';
import { checkFlag } from '@/lib/statsig';
import { SCAccordion, SCForm, SCFormStep, SCParagraph, SCProperty, SCPropertyList, SCRadio, SCRadioGroup, SCSidebar } from '@scale-computing/scale-design-framework';
import { compareVersions } from 'compare-versions';
import { rg4js } from '../../lib/raygun';
export default {
    components: { SCAccordion, SCForm, SCFormStep, SCParagraph, SCProperty, SCPropertyList, SCRadio, SCRadioGroup, SCSidebar },
    data() {
        return {
            selectedVersion: null,
            manualUUID: null,
            visible: false,
            loading: false,
            loaded: false,
            clusterId: null,
            cluster: null,
        };
    },
    mounted() {
        EventBus.on('show-cluster-updates-available-modal', (payload) => {
            this.show(payload.clusterId);
        });
    },
    methods: {
        handleDialogClose() {
            this.visible = false;
        },
        show(clusterId) {
            // reset before loading so a reopen doesn't flash the previous cluster
            this.clusterId = clusterId;
            this.cluster = null;
            this.loaded = false;
            this.selectedVersion = null;
            this.visible = true;
            this.load();
        },
        async load() {
            try {
                this.loading = true;
                // clusters-fleet: fleet's v3 cluster has the same update fields
                const getById = checkFlag('clusters-fleet') ? cluster.getById3 : cluster.getById2;
                const response = await getById({ id: this.clusterId });
                this.cluster = response;
                this.cluster.data.updatesAvailableOptions.sort((a,b) => -compareVersions(a.uuid, b.uuid));
                this.selectedVersion = this.cluster.data.updatesAvailableOptions[0]?.uuid ?? null; //default to first.
                this.loaded = true;
            } catch (e) {
                rg4js('send', { error: e});
                this.$toast.add({ group: "messages",  severity: 'error', summary: `Unable to load cluster`, detail: e.message });
            } finally {
                this.loading = false;
            }
        },
        // SCForm's save flow drives the saving state via done()
        async handleApplySave(data, done) {
            const uuid = this.selectedVersion;
            if (!uuid) return done();
            try {
                const commandSend = checkFlag('clusters-fleet') ? cluster.commandSend3 : cluster.commandSend;
                const response = await commandSend({
                    id: this.clusterId,
                    command: 'IcosUpdateApply',
                    payload: { uuid },
                });
                // both clients report 401/403/404 through the global error
                // banner and resolve with no data; don't toast success on top
                if (!response?.data) return done();

                // show the cluster as updating until it reports the new cycle
                markUpdateSent(this.clusterId, this.cluster.data.updatesStatus);

                done();
                this.handleDialogClose();
                this.$toast.add({ group: 'messages', severity: 'success', summary: 'Update request sent. The update will start in a few moments.', life: 5000 });
            } catch (e) {
                done(e.message);
                rg4js('send', { error: e });
                this.$toast.add({ group: 'messages', severity: 'error', summary: 'Error sending update request', detail: e.message });
            }
        },
    },
};
</script>
